import { useEffect, useState, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { getMasterclassCasesRoute, ROUTES } from '@/config/routes';
import { useMasterclass } from '@/contexts/MasterclassContext';
import { useCaseProgressByMasterclassAndCase, useAuthUser } from '@/hooks/queries';
import { CaseRenderer } from '@/modules/case';

/**
 * Case detail page that displays a specific case within a masterclass.
 *
 * Route: /masterclass/:masterclassId/:caseId
 *
 * Ensures the case session exists before rendering the case.
 */
export default function Index() {
  const { masterclassId, caseId } = useParams<{ masterclassId: string; caseId: string }>();
  const { setSelectedCase, instantiateCaseProgress } = useMasterclass();
  const { data: user } = useAuthUser();
  const userId = user?.id || null;

  // Query the specific case progress - pass masterclassId AND caseId
  // MasterClassUserSession is now keyed by (userId, masterclassId, caseId)
  const {
    data: caseProgressData,
    isLoading: progressLoading,
    refetch,
  } = useCaseProgressByMasterclassAndCase(userId, masterclassId || null, caseId || null);

  const [isSessionReady, setIsSessionReady] = useState(false);
  const hasAttemptedCreation = useRef(false);

  // Create session if it doesn't exist (only once)
  useEffect(() => {
    // Skip if we don't have required data or already loading
    if (!masterclassId || !caseId || !userId || progressLoading) return;

    // If we have data, session exists - mark ready
    if (caseProgressData) {
      setIsSessionReady(true);
      return;
    }

    // Only attempt creation once per mount
    if (hasAttemptedCreation.current) {
      // Already tried creating, just mark ready to avoid infinite loop
      setIsSessionReady(true);
      return;
    }

    // No session found - create one
    hasAttemptedCreation.current = true;
    console.log('[Index] Creating new session for masterclass and case:', masterclassId, caseId);

    instantiateCaseProgress(masterclassId, caseId)
      .then(() => refetch())
      .then(() => setIsSessionReady(true))
      .catch((error) => {
        console.error('[Index] Failed to create session:', error);
        // Still mark as ready to avoid blocking UI
        setIsSessionReady(true);
      });
  }, [
    masterclassId,
    caseId,
    userId,
    progressLoading,
    caseProgressData,
    instantiateCaseProgress,
    refetch,
  ]);

  // Redirect if missing required params
  if (!caseId) {
    if (masterclassId) {
      return <Navigate to={getMasterclassCasesRoute(masterclassId)} replace />;
    }
    return <Navigate to={ROUTES.MASTERCLASS_HOME} replace />;
  }

  // Synchronize context with URL
  setSelectedCase(caseId);

  // Wait for session to be ready
  if (!userId || progressLoading || !isSessionReady) {
    return <Skeleton className="h-full w-full" />;
  }

  return <CaseRenderer caseId={caseId} masterclassId={masterclassId} />;
}
